import { Grid } from '@mui/material'

import { DeviceTableRow } from '../../routes/accounts/components/devices-tab-panel/types'
import useTemperatures from '../../utils/hooks/useTemperatures'

import { NET_TYPE_MAP } from './net-type-bubble'
import ChannelBubble from '.'

const ChannelBubbleGroup = ({ device }: { device: IDeviceAPIObject | DeviceTableRow }) => {
  const { formatTemperature } = useTemperatures()

  const { lastCheckIn, alerts: deviceAlerts, hasOfflineAlert, nodeType, currentValues } = device
  const alerts = deviceAlerts.filter((a) => a.status === 'Active') // Only show active alerts

  if (!lastCheckIn) return null

  const {
    signal_indicator: signalIndicator,
    battery_indicator: batteryIndicator,
    temp,
    leak,
    probe,
    probe_temp: probeTemp,
    hum,
    valve,
    leak_probe: leakProbe,
    nh3,
    warning_nh3: warningNh3,
    powersource,
    defroute,
    water_pressure: waterPressure,
    temp_unit: tempUnit,
    msg_type: msgType,
  } = currentValues

  // For leak5s devices that report a msg_type channel (Slim 2.0), only show signal
  // strength when the device is connected via LoRa.
  const msgTypeValue = String(msgType?.value ?? '').toLowerCase()
  const netTypeName = NET_TYPE_MAP[msgTypeValue]?.name
  const isLoraConnection = msgTypeValue === 'lora'
  // Only show the msg_type bubble when the channel has a non-empty value
  const effectiveMsgType = msgType?.value ? msgType : undefined
  const effectiveSignalIndicator = nodeType.name === 'leak5s' && !isLoraConnection ? undefined : signalIndicator

  let tempLabel = 'Temp.'
  if (nodeType.name === 'bvs' || nodeType.name === 'bvs2') tempLabel = 'Ambient Temp.'
  if (nodeType.name === 'aqua_scope_monitor_ps') tempLabel = 'Water Temp.'

  let tempChannel = temp
  if (tempChannel) {
    if (nodeType.name === 'bvs' || nodeType.name === 'bvs2') {
      tempChannel = { ...temp, value: formatTemperature(temp.value as number, 'integer', tempUnit?.value !== 'fahrenheit' ? 'degC' : 'degF') }
    } else {
      tempChannel = { ...temp, value: formatTemperature(temp.value as number, 'integer') }
    }
  }

  let probeChannel = probe
  if (probeChannel) {
    probeChannel = { ...probe, value: formatTemperature(probe.value as number, 'integer') }
  }

  let probeTempChannel = probeTemp
  if (probeTempChannel) {
    probeTempChannel = { ...probeTemp, value: formatTemperature(probeTemp.value as number, 'integer') }
  }

  let humChannel = hum
  if (humChannel) {
    humChannel = { ...hum, value: `${Math.round(Number(hum.value))}%` }
  }

  return (
    <Grid
      container={true}
      sx={{
        gap: 1.5,
      }}
    >
      <ChannelBubble
        channel={effectiveSignalIndicator}
        defrouteChannel={defroute}
        channelName="signal_indicator"
        label="Signal"
        alerts={alerts}
        isOffline={hasOfflineAlert}
        nodeType={nodeType}
      />
      <ChannelBubble channel={effectiveMsgType} channelName="msg_type" label="Net." alerts={alerts} isOffline={hasOfflineAlert} channelTooltip={netTypeName} />
      <ChannelBubble channel={batteryIndicator} channelName="battery_indicator" label="Battery" alerts={alerts} isOffline={hasOfflineAlert} />
      <ChannelBubble channel={tempChannel} channelName="temp" label={tempLabel} alerts={alerts} isOffline={hasOfflineAlert} />
      <ChannelBubble channel={leak} channelName="leak" label="Water" alerts={alerts} isOffline={hasOfflineAlert} />
      <ChannelBubble channel={probeChannel} channelName="probe" label="Probe" alerts={alerts} isOffline={hasOfflineAlert} />
      <ChannelBubble channel={probeTempChannel} channelName="probe_temp" label="Probe Temp." alerts={alerts} isOffline={hasOfflineAlert} />
      <ChannelBubble channel={humChannel} channelName="hum" label="Hum." alerts={alerts} isOffline={hasOfflineAlert} />
      <ChannelBubble channel={valve} channelName="valve" label="Valve Position" alerts={alerts} isOffline={hasOfflineAlert} />
      <ChannelBubble channel={leakProbe} channelName="leak_probe" label="Leak Probe" alerts={alerts} isOffline={hasOfflineAlert} />
      <ChannelBubble channel={nh3} channelName="nh3" label="Amm. Level" alerts={alerts} isOffline={hasOfflineAlert} />
      <ChannelBubble channel={warningNh3} channelName="warning_nh3" label="Amm. Warning" alerts={alerts} isOffline={hasOfflineAlert} />
      <ChannelBubble channel={powersource} channelName="powersource" label="Power Source" alerts={alerts} isOffline={hasOfflineAlert} nodeType={nodeType} />
      <ChannelBubble channel={defroute} channelName="defroute" label="Ethernet" alerts={alerts} isOffline={hasOfflineAlert} />
      <ChannelBubble channel={waterPressure} channelName="water_pressure" label="Pressure" alerts={alerts} isOffline={hasOfflineAlert} />
    </Grid>
  )
}

export default ChannelBubbleGroup
